import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { jmd } from "@/lib/format";
import { CreditCard, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

export default function PayNow() {
  const [enabled, setEnabled] = useState(null);
  const [amount, setAmount] = useState("");
  const [reference, setReference] = useState("");
  const [description, setDescription] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/payments/fygaro/status").then(({ data }) => setEnabled(!!data.enabled)).catch(() => setEnabled(false));
  }, []);

  const pay = async () => {
    const amt = Number(amount);
    if (!amt || amt <= 0) { toast.error("Enter an amount greater than 0"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/payments/fygaro/pay-now", { amount: amt, reference: reference || undefined, description: description || undefined });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start payment");
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-4 sm:px-6 py-12" data-testid="paynow-page">
      <div className="bg-card rounded-3xl border border-border p-7 sn-noise">
        <div className="flex items-center gap-2 text-coral font-bold text-xs uppercase tracking-widest">
          <CreditCard size={15} /> Fygaro Pay Now
        </div>
        <h1 className="font-display text-2xl font-bold tracking-tight mt-3">Make a payment</h1>
        <p className="text-sm text-muted-foreground mt-1">Pay an invoice, registration or any amount securely by card via Fygaro.</p>

        {enabled === false && (
          <div className="mt-5 rounded-xl bg-secondary/60 p-4 text-sm text-muted-foreground" data-testid="paynow-disabled">
            Fygaro payments are not set up yet. Add your Fygaro API credentials to enable this.
          </div>
        )}

        <div className={`mt-6 space-y-3 ${enabled === false ? "opacity-50 pointer-events-none" : ""}`}>
          <label className="block text-xs font-semibold text-muted-foreground">Amount (JMD)
            <input type="number" min="1" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00"
              className="w-full rounded-xl border border-border px-4 py-3 text-sm mt-1 font-normal text-ink" data-testid="paynow-amount" />
          </label>
          {amount > 0 && <div className="text-sm font-semibold text-emerald">You'll pay {jmd(Number(amount))}</div>}
          <label className="block text-xs font-semibold text-muted-foreground">Reference (optional)
            <input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="e.g. Invoice #1024"
              className="w-full rounded-xl border border-border px-4 py-3 text-sm mt-1 font-normal text-ink" data-testid="paynow-reference" />
          </label>
          <label className="block text-xs font-semibold text-muted-foreground">Description (optional)
            <input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="What is this payment for?"
              className="w-full rounded-xl border border-border px-4 py-3 text-sm mt-1 font-normal text-ink" data-testid="paynow-description" />
          </label>
          <button onClick={pay} disabled={loading} data-testid="paynow-submit-btn"
            className="w-full flex items-center justify-center gap-2 bg-coral text-white rounded-xl py-3.5 font-semibold hover:bg-ink transition-colors disabled:opacity-60">
            {loading ? <Loader2 size={18} className="animate-spin" /> : <CreditCard size={18} />} Pay Now
          </button>
          <p className="flex items-center gap-1.5 text-xs text-muted-foreground justify-center">
            <ShieldCheck size={13} className="text-emerald" /> Secure hosted payment page by Fygaro
          </p>
        </div>
      </div>
    </div>
  );
}
